import React, { useState, useContext } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { LoruleRequestConfig } from '@galaxy/lorule-react-commons';
import { Modal, Button, Alert } from 'react-bootstrap';
import { ResponseError, SubmitButton, AsyncSelectField } from '@nintendo/forms';
import ConfigContext from '../../../../contexts/ConfigContext';
import NinModal from '../../../common/modal/NinModal';
import { sendToast, noOptionsHandler, formatAmDisplayString } from '../../../../utils/displayUtils';
import TextCountedField from '../../../common/TextCountedField';
import { COMMENT_MAX_LENGTH, INITIAL_SELECT_ID } from '../../../../utils/Constants';
import { BulkUserItemType, UserManagementItemType } from '../../../../api/userManagementApiTypes';
import { replaceUsers, removeUsers } from '../../../../api/userManagementApi';
import { UserManagementFormType } from './userManagementFormType';
import { getAmUsers } from '../../../../api/amUserApi';

export interface UserManagementFormBaseProps {
    title: String;
    formType: UserManagementFormType;
    toggleButton: JSX.Element;
    onSubmit?: () => void;
}

export interface UserManagementModalProps extends UserManagementFormBaseProps {
    personId: number;
    personName: string;
    bulkSelect?: boolean;
    bulkSelectItems: UserManagementItemType[];
}

const submitUserManagement = async (
    config: LoruleRequestConfig,
    formType: UserManagementFormType,
    values: {
        personId: number;
        projectAdmin: boolean;
        commentMessage: string | null;
    },
    personName: string,
    bulkSelectItems: BulkUserItemType[],
    bulkSelect: boolean,
    setResponseError: (errorMessage: string) => void,
    resetForm: () => void,
    setSubmitting: (isSubmitting: boolean) => void,
    toggleModal: () => void,
) => {
    try {
        let actionText = '';
        if (formType === UserManagementFormType.REPLACE && values.personId && values.personId > 0) {
            actionText = 'Replaced User';
            await replaceUsers(
                config,
                values.personId,
                bulkSelectItems,
                values.commentMessage ? values.commentMessage : null,
            );
        } else {
            actionText = 'Removed User';
            await removeUsers(
                config,
                bulkSelectItems,
                values.commentMessage ? values.commentMessage : null,
            );
        }
        if (!bulkSelect) {
            sendToast(
                <>
                    {actionText}: <strong>{personName}</strong>
                </>,
            );
        } else {
            sendToast(
                <>
                    {actionText} on <strong>{bulkSelectItems.length}</strong> AM
                    {bulkSelectItems.length > 1 ? 's' : ''}
                </>,
            );
        }

        resetForm();
        toggleModal();

        return true;
    } catch (error) {
        if (error instanceof Error) {
            setResponseError(
                `${
                    formType === UserManagementFormType.REPLACE ? 'Replace' : 'Remove '
                } user failed:  ${error.message}`,
            );
        }
        setSubmitting(false);
        return false;
    }
};

const UserManagementModal = (props: UserManagementModalProps) => {
    const config = useContext(ConfigContext);

    const [responseError, setResponseError] = useState('');

    const addCommentValidationSchema = Yup.object().shape({
        personId: Yup.number()
            .required('Person is required')
            .test(
                'id validation',
                'Person is required',
                (personId) =>
                    props.formType === UserManagementFormType.REMOVE ||
                    personId !== INITIAL_SELECT_ID,
            ),
        commentMessage: Yup.string()
            // eslint-disable-next-line no-template-curly-in-string
            .max(COMMENT_MAX_LENGTH, 'Comment exceeds max length of ${max} characters'),
    });

    const initialValues = {
        personId: INITIAL_SELECT_ID,
        projectAdmin: false,
        commentMessage: '',
    };

    let actionText = '';
    if (props.formType === UserManagementFormType.REPLACE) {
        actionText = 'Replace Users';
    } else if (props.formType === UserManagementFormType.REMOVE) {
        actionText = 'Remove Users';
    }

    return (
        <NinModal
            button={props.toggleButton}
            size="lg"
            render={(toggleModal: () => void) => (
                <Formik
                    initialValues={initialValues}
                    validationSchema={addCommentValidationSchema}
                    onSubmit={async (values, { resetForm, setSubmitting }) => {
                        const submitSuccessful = await submitUserManagement(
                            config,
                            props.formType,
                            values,
                            props.personName,
                            props.bulkSelectItems,
                            props.bulkSelect || false,
                            setResponseError,
                            resetForm,
                            setSubmitting,
                            toggleModal,
                        );
                        if (props.onSubmit && submitSuccessful) {
                            setResponseError('');
                            props.onSubmit();
                        }
                    }}
                    render={({ values, touched, errors, isSubmitting }) => (
                        <>
                            <Modal.Header closeButton>
                                <Modal.Title>
                                    {props.bulkSelect ? 'Bulk ' : ''}
                                    {actionText}
                                    {props.bulkSelect ? ' in Selected Action Memo' : ''}
                                    {props.bulkSelectItems.length > 1 ? 's' : ''}
                                </Modal.Title>
                            </Modal.Header>
                            <Form>
                                <Modal.Body>
                                    <ResponseError errorMessage={responseError} />

                                    {props.bulkSelectItems.length > 100 && (
                                        <Alert variant="warning">
                                            Attempting to update more than <b>100</b> records in a
                                            single bulk update is not recommended as it will tie up
                                            too much system resources. (Currently,{' '}
                                            <b>{props.bulkSelectItems.length}</b> records are
                                            selected.)
                                        </Alert>
                                    )}

                                    {props.formType === UserManagementFormType.REPLACE && (
                                        <Alert variant="info">
                                            Please note if the replacment already exists on the
                                            action memo, then the user will be removed/skipped
                                            instead.
                                        </Alert>
                                    )}

                                    {props.formType === UserManagementFormType.REPLACE && (
                                        <AsyncSelectField
                                            label="Person"
                                            name="personId"
                                            touched={touched.personId}
                                            errors={errors.personId}
                                            testId="personId"
                                            isRequired
                                            asyncSelectProps={{
                                                loadOptions: async (inputValue: string) => {
                                                    if (inputValue.length > 2) {
                                                        const users = await getAmUsers(config, {
                                                            name: inputValue,
                                                        });
                                                        if (users) {
                                                            return users
                                                                .filter(
                                                                    (user) =>
                                                                        user.personId !==
                                                                        props.personId,
                                                                )
                                                                .map((user) => ({
                                                                    value: user.personId.toString(),
                                                                    label: `${user.preferredName} (${user.title})`,
                                                                }));
                                                        }
                                                    }
                                                    return [];
                                                },
                                                noOptionsMessage: noOptionsHandler,
                                            }}
                                        />
                                    )}
                                    {props.bulkSelect && (
                                        <div className="form-group row">
                                            <div className="col-sm-5 col-form-label">
                                                Selected Action Memos
                                            </div>
                                            <div className="col-sm-7 mt-2">
                                                <ul>
                                                    {props.bulkSelectItems
                                                        .slice(0, 10)
                                                        .map((bulkSelectItem) => (
                                                            <li>
                                                                {formatAmDisplayString(
                                                                    bulkSelectItem.amNumber,
                                                                    bulkSelectItem.supNumber,
                                                                )}
                                                            </li>
                                                        ))}
                                                    {props.bulkSelectItems.length > 10 && (
                                                        <li>
                                                            ...and{' '}
                                                            {props.bulkSelectItems.length - 10} more
                                                        </li>
                                                    )}
                                                </ul>
                                            </div>
                                        </div>
                                    )}
                                    <TextCountedField
                                        label="Comment (Optional)"
                                        name="commentMessage"
                                        touched={touched.commentMessage}
                                        errors={errors.commentMessage}
                                        testId="commentMessage"
                                        length={
                                            values.commentMessage ? values.commentMessage.length : 0
                                        }
                                        maxLength={COMMENT_MAX_LENGTH}
                                    />
                                </Modal.Body>
                                <Modal.Footer>
                                    <Button
                                        variant="outline-secondary"
                                        onClick={toggleModal}
                                        disabled={isSubmitting}
                                    >
                                        Close
                                    </Button>
                                    <SubmitButton
                                        className="btn-primary ml-auto"
                                        disabled={
                                            props.formType === UserManagementFormType.REPLACE &&
                                            values.personId === INITIAL_SELECT_ID
                                        }
                                        isSubmitting={isSubmitting}
                                        buttonText={actionText}
                                    />
                                </Modal.Footer>
                            </Form>
                        </>
                    )}
                />
            )}
        />
    );
};

export default UserManagementModal;
